import { invariant } from '../../util/invariant';
import type { StructuredLogger } from '@mimir/primitive/structuredLogger';
import { MimirIndexFileGenerator } from './indexGenerator';
import type { MimirIndexEntry, MimirIndexFile, MimirIndexHeader } from './mimirIndexFile';
import { IncrementalIndexSerializer, serializeMimirIndexFile } from './mimirIndexFile';
import type { ParsedKLV } from './klv';
import { bytesToKlv } from './klv';

export function mxfToIndex(structuredLogger: StructuredLogger): TransformStream<Uint8Array, Uint8Array> {
  const { writable, readable: klv } = bytesToKlv(structuredLogger);
  const index = klv.pipeThrough(klvToIndex(structuredLogger));
  return { writable, readable: index };
}

export function klvToIndex(structuredLogger: StructuredLogger): TransformStream<ParsedKLV, Uint8Array> {
  let generator : MimirIndexFileGenerator | null = null;
  return new TransformStream<ParsedKLV, Uint8Array>({
    start: async (controller) => {
      generator =
          new MimirIndexFileGenerator(
            structuredLogger,
            {
              onIndex(mimirIndexFile: MimirIndexFile) {
                structuredLogger.log({ level: 'info', message: `Fixed frame size index: ${JSON.stringify(mimirIndexFile)}` });
                controller.enqueue(serializeMimirIndexFile(mimirIndexFile));
                controller.terminate();
              },
              createIndexEntryHandler(header: MimirIndexHeader) {
                structuredLogger.log({ level: 'info', message: `Variable frame size index. header=${JSON.stringify(header)}` });
                const incremental =
                  new IncrementalIndexSerializer(
                    header,
                    (chunk: Uint8Array) => {
                      controller.enqueue(chunk);
                    }
                  );

                return {
                  onEntry(indexEntry: MimirIndexEntry) {
                    incremental.writeEntry(indexEntry);
                  },
                  close() { }
                };
              },
            }
          );
    },
    transform: (klv: ParsedKLV, controller) => {
      invariant(generator !== null, 'Expected to have generator at this point');
      try {
        generator?.parseKlv(klv);
      } catch (error: any) {
        structuredLogger.log({ level: 'warn', message: `MXF index generation error: ${error}` });
        controller.terminate();
        throw error;
      }
    },
    flush: () => {
      structuredLogger.log({ level: 'warn', message: 'Flushing mxfToIndex' });
      invariant(generator !== null, 'Expected to have generator at this point');
      generator.flush();
    },
  });
}

